import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import { useTranslation } from "react-i18next";
import { FormHelperText, Popover } from "@mui/material";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
    CustomStyleFormHelperText,
    CustomStyleSelect,
    CustomStyleSelectMultiple,
    CustomStyleSelectRequired
} from "./Styles";
import CustomChip from "./CustomChip";
import {CrossIconsOptions, CloseIcon, DownArrowIcon, IconCheckboxChecked, IconCheckboxUnchecked} from "./buttons/IconButtons";
import { colors } from '../enums/colors';
import { POSITION_MODEL } from '../Helpers/PositionModel';
import CustomTooltip from "./CustomTooltip";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * @Description Multi-select dropdown with chip display, overflow popover,
 * and scroll-position preservation on each selection change.
 *
 * @param String id: Identifier for the select (required)
 * @param String name: Name for the select (optional)
 * @param Array<Object> list: List of options [{ id, name }] (required)
 * @param Object inputProps: Attributes applied to the native select (optional)
 * @param Array value: Selected values — array of keyName values (required)
 * @param Boolean disabled: Enable/disable interaction (optional)
 * @param String trans: Label translation key (required)
 * @param Boolean emptyFields: Indicates if the field is empty (optional)
 * @param Boolean required: Indicates if the select is required (optional)
 * @param String keyName: Property used as option value (default: "name")
 * @param String helperText: Text displayed under the input field (optional)
 * @param Boolean marginBottom: Controls bottom margin on FormControl (optional)
 * @param Event onChange: onChange trigger — receives synthetic event with value array (optional)
 * @param Event onFocus: onFocus trigger (optional)
 */
export const CustomSelectMultiple = ({
    id, name, list, inputProps, value = [], disabled, trans,
    emptyFields, required, keyName = "name", helperText = "",
    marginBottom = true, onChange, onFocus, placeholder=""
}) => {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState(null);
    const [visibleCount, setVisibleCount] = useState(Infinity);
    const popoverOpen = Boolean(anchorEl);
    const containerRef = useRef(null);
    const formControlRef = useRef(null);
    const menuListRef = useRef(null);
    const moreChipRef = useRef(null);
    const savedScrollRef = useRef(0);

    const MenuProps = {
        variant: "menu",
        PaperProps: {
            className: "overflow-y-auto maxh-40vh"
        },
        sx: CustomStyleSelectMultiple.MuiPaper,
    };
    const isEmpty = !value || value.length === 0;
    const hasError = emptyFields && isEmpty;

    const selectedItems = value
        .map(val => list?.find(i => i[keyName] === val))
        .filter(Boolean);

    /**
     * Resets visibleCount to Infinity whenever the number of selected items changes,
     * triggering a fresh chip-fitting measurement on the next layout pass.
     */
    useLayoutEffect(() => {
        setVisibleCount(Infinity);
    }, [selectedItems.length]);

    /**
     * Measures the rendered chip widths and determines how many chips fit
     * within the available container width. Reserves space for the "+N more"
     * chip when not all chips fit. Runs only when visibleCount is Infinity
     * (i.e., after a reset) and the DOM chips match the selected items count.
     */
    useLayoutEffect(() => {
        if (visibleCount !== Infinity) return;
        if (!containerRef.current || selectedItems.length === 0) return;

        const container = containerRef.current;
        const chipEls = Array.from(container.querySelectorAll('[data-chip-item]'));
        if (chipEls.length !== selectedItems.length) return;

        const containerWidth = formControlRef.current?.clientWidth ?? container.clientWidth;
        const GAP = 8;
        const moreChipWidth = (moreChipRef.current?.offsetWidth ?? 72) + GAP;
        let totalWidth = 0;
        let count = 0;

        for (let i = 0; i < chipEls.length; i++) {
            const chipWidth = chipEls[i].offsetWidth + GAP;
            const needsMore = i < chipEls.length - 1;
            if (totalWidth + chipWidth + (needsMore ? moreChipWidth : 0) > containerWidth) break;
            totalWidth += chipWidth;
            count++;
        }

        setVisibleCount(Math.max(1, count < selectedItems.length ? count : selectedItems.length));
    }, [visibleCount, selectedItems.length]);

    const safeVisibleCount = Number.isFinite(visibleCount) ? visibleCount : selectedItems.length;
    const hiddenCount = Math.max(0, selectedItems.length - safeVisibleCount);
    const visibleItems = selectedItems.slice(0, safeVisibleCount);
    const hiddenItems = selectedItems.slice(safeVisibleCount);

    /**
     * Restores the dropdown scroll position after a value change.
     * MUI remounts the menu list on each selection, so scroll resets to 0
     * without this; rAF defers the assignment until after the DOM update.
     */
    useEffect(() => {
        if (!menuListRef.current || !document.contains(menuListRef.current)) return;
        const el = menuListRef.current;
        const saved = savedScrollRef.current;
        const id = requestAnimationFrame(() => { el.scrollTop = saved; });
        return () => cancelAnimationFrame(id);
    }, [value]);

    /**
     * Closes the overflow popover when all hidden chips are removed,
     * preventing a stale open popover with no content.
     */
    useEffect(() => {
        if (hiddenCount === 0 && anchorEl) setAnchorEl(null);
    }, [hiddenCount]);

    /**
     * Saves the current dropdown scroll offset before a selection change
     * so it can be restored by the value useEffect above.
     */
    const handleMenuItemClick = (e) => {
        savedScrollRef.current = menuListRef.current?.scrollTop ?? 0;
    };

    /**
     * Removes a single item from the selection and notifies the parent
     * via a synthetic onChange event matching the native select signature.
     *
     * @param {*} val - The keyName value of the chip to remove.
     */
    const handleDelete = (val) => {
        onChange?.({ target: { value: value.filter(v => v !== val) } });
    };

    return (
        <div className="position-relative">
            <FormControl
                ref={formControlRef}
                sx={hasError ? CustomStyleSelectRequired : CustomStyleSelect(marginBottom)}
                error={hasError}
            >
                <InputLabel disabled={disabled} shrink={value.length > 0 || Boolean(placeholder)}>
                    {t(trans)}{required === true && " *"}
                </InputLabel>
                <Select
                    size="small"
                    multiple
                    name={name}
                    value={value}
                    required={required}
                    disabled={disabled}
                    onFocus={onFocus}
                    onChange={onChange}
                    inputProps={{ ...inputProps, id }}
                    displayEmpty
                    renderValue={() => placeholder
                        ? <span className="font-color-general-950 fs-13px">{placeholder}</span>
                        : null
                    }
                    MenuProps={MenuProps}
                >
                    {undefined !== list && list.length > 0 ? (
                        list.map((i, index) => (
                            <MenuItem
                                id={`mi_csm_selection_${index}`}
                                key={i.id}
                                value={i[keyName]}
                                onClick={handleMenuItemClick}
                                onMouseDown={(e) => e.preventDefault()}
                                className={`my-1 py-0 pl-0 pr-4 border-radius-8px ${value.includes(i[keyName]) ?
                                    'border-full-secondary-150 background-secondary-45' : ''}`}
                            >
                                <Checkbox
                                    checked={value.includes(i[keyName])}
                                    icon={<IconCheckboxUnchecked className="icon-color-general-950" />}
                                    checkedIcon={<IconCheckboxChecked className="icon-color-shades-800" />}
                                    disableRipple={true}
                                    classes={"p-0 m-0"}
                                />
                                <ListItemText primary={<CustomTooltip
                                    text={i.name}
                                    classes={`text-ellipsis-1-row`}
                                    showIfTruncated={true}
                                />}/>
                            </MenuItem>
                        ))
                    ) : (
                        <MenuItem id="mi_csm_no_options" value={null} disabled>
                            <ListItemText primary={<CustomTooltip
                                text={t("no_options").toUpperCase()}
                                classes={`text-ellipsis-1-row`}
                                showIfTruncated={true}
                            />}/>
                        </MenuItem>
                    )}
                </Select>

                {hasError && (
                    <FormHelperText
                        id="outlined-weight-helper-text"
                        error={true}
                        sx={CustomStyleFormHelperText(false, marginBottom)}
                    >
                        {t("requiredField")}
                    </FormHelperText>
                )}

                {helperText && !emptyFields && (
                    <FormHelperText
                        className="css-k4qjio-MuiFormHelperText-root"
                        sx={CustomStyleFormHelperText(false, marginBottom)}
                    >
                        {helperText}
                    </FormHelperText>
                )}
            </FormControl>

            {selectedItems.length > 0 && (
                <div
                    ref={containerRef}
                    className="position-absolute top-100 start-0 w-100 d-flex mt-1 gap-2 z-3"
                >
                    {visibleItems.map((item) => (
                        <div key={item[keyName]} data-chip-item className="minw-60px flex-shrink-0">
                            <CustomChip
                                legend={ <CustomTooltip
                                    text={item.name}
                                    classes={`text-ellipsis-1-row`}
                                    showIfTruncated={true}
                                />
                                }
                                className={"flex-grow-0"}
                                background={colors.shades100}
                                borderColor={colors.shades300}
                                fontColor={colors.general950}
                                onDelete={() => handleDelete(item[keyName])}
                                deleteIcon={<div><CrossIconsOptions height={16} width={16} className="icon-color-shades-400"/></div>}
                                variant={"large"}
                            />
                        </div>
                    ))}
                    <div
                        ref={moreChipRef}
                        className={`flex-shrink-0${hiddenCount === 0 ? ' invisible position-absolute' : ''}`}
                    >
                        <CustomChip
                            legend={`+${hiddenCount || selectedItems.length} ${t("more").toLowerCase()}`}
                            background={colors.transparent}
                            borderColor={colors.secondary600}
                            fontColor={colors.secondary600}
                            onClick={hiddenCount > 0 ? (e) => setAnchorEl(e.currentTarget) : undefined}
                            className={hiddenCount > 0 ? "cursor-pointer" : ""}
                            variant={"large"}
                            icon={DownArrowIcon}
                            iconPosition={POSITION_MODEL.RIGHT}
                            classNameIcon={"icon-color-secondary-600"}
                        />
                        {hiddenCount > 0 && <Popover
                                open={popoverOpen}
                                anchorEl={anchorEl}
                                onClose={() => setAnchorEl(null)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                PaperProps={{
                                    className: "background-general-00 border-full-shades-400 border-radius-8px mt-2",
                                    sx: { maxWidth: formControlRef.current?.clientWidth }
                                }}
                            >
                                <div className="position-absolute r-6px">
                                    <CloseIcon
                                        height={12} width={12}
                                        className="icon-color-shades-400 cursor-pointer"
                                        onClick={() => setAnchorEl(null)}
                                    />
                                </div>
                                <div className="py-3 pl-3 pr-4 p d-flex flex-wrap gap-2 overflow-y-auto maxh-200px minw-180px maxw-90vw">
                                    {hiddenItems.map((item) => (
                                        <div key={item[keyName]} className="mw-100 overflow-hidden">
                                            <CustomChip
                                                legend={<CustomTooltip
                                                    text={item.name}
                                                    classes="text-ellipsis-1-row"
                                                    showIfTruncated={true}
                                                />}
                                                className="flex-grow-0 minw-0 mw-100"
                                                background={colors.shades100}
                                                borderColor={colors.shades300}
                                                fontColor={colors.general950}
                                                onDelete={() => handleDelete(item[keyName])}
                                                deleteIcon={<div><CrossIconsOptions height={16} width={16} className="icon-color-shades-400"/></div>}
                                                variant={"large"}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </Popover>}
                    </div>
                </div>
            )}
        </div>
    );
};
